/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import utils from '../utils';
import buildCatalogueQueryParams from './buildCatalogueQueryParams';
import { getPromptV2Client, handlePromptServiceError } from './promptV2Client';
import PROMPT_V2_SERVICE_TYPE from './promptV2ServiceConstants';

export default class PromptV2Service extends BaseService {
  constructor({
    serviceType,
    eventName,
    statusKey,
    storeName,
    errorLangKey,
    errorEvent,
    handleUpdateMethod = 'handleUpdateResponse'
  }) {
    super(eventName, statusKey);
    this.serviceType = serviceType;
    this.storeName = storeName;
    this.errorLangKey = errorLangKey;
    this.errorEvent = errorEvent;
    this.handleUpdateMethod = handleUpdateMethod;
  }

  async handleEvent(eventNameArg, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { client, headers: reqHeaders } = await getPromptV2Client(this, headers);

    switch (this.serviceType) {
      case PROMPT_V2_SERVICE_TYPE.CATALOGUE:
        return this.fetchCatalogue({ payload, client, reqHeaders });
      case PROMPT_V2_SERVICE_TYPE.VERSION:
        return this.fetchVersion({ payload, client, reqHeaders });
      case PROMPT_V2_SERVICE_TYPE.VERSIONS:
        return this.fetchVersions({ payload, client, reqHeaders });
      case PROMPT_V2_SERVICE_TYPE.UPDATE:
        return this.updatePrompt({ payload, client, reqHeaders });
      case PROMPT_V2_SERVICE_TYPE.NAME_AVAILABILITY:
        return this.fetchNameAvailability({ payload, client, reqHeaders });
      case PROMPT_V2_SERVICE_TYPE.VERSION_GRAPHS:
        return this.fetchVersionGraphs({ payload, client, reqHeaders });
      default:
        throw new Error(`Unsupported prompt v2 service type: ${this.serviceType}`);
    }
  }

  async fetchCatalogue({ payload, client, reqHeaders }) {
    const { search = '', agenticOnly = true, dataContext = {}, offset = 0, limit = 50, type } = payload || {};
    const params = buildCatalogueQueryParams({ search, agenticOnly, offset, limit, type });
    const path = `/prompts/catalogue?${params.toString()}`;
    // Correlate via axios config so concurrent catalogue requests cannot cross-wire responses.
    return client.get(path, {
      headers: reqHeaders,
      promptRequestMeta: { search, dataContext }
    });
  }

  async fetchVersion({ payload, client, reqHeaders }) {
    const { promptRef, promptVersionRef } = payload;
    return client.get(`/prompts/${encodeURIComponent(promptRef)}/versions/${encodeURIComponent(promptVersionRef)}`, {
      headers: reqHeaders,
      promptRequestMeta: { promptRef, promptVersionRef }
    });
  }

  async fetchVersions({ payload, client, reqHeaders }) {
    const { promptRef } = payload;
    const response = await client.get(`/prompts/${encodeURIComponent(promptRef)}/versions`, {
      headers: reqHeaders
    });
    this.responsePromptRefs = this.responsePromptRefs || new WeakMap();
    this.responsePromptRefs.set(response, promptRef);
    return response;
  }

  async fetchNameAvailability({ payload, client, reqHeaders }) {
    const { name = '', excludePromptId } = payload || {};
    const params = new URLSearchParams();
    params.set('name', name);
    if (excludePromptId) {
      params.set('excludePromptId', excludePromptId);
    }
    return client.get(`/prompts/names/availability?${params.toString()}`, { headers: reqHeaders });
  }

  async fetchVersionGraphs({ payload, client, reqHeaders }) {
    const { promptRef, promptVersionRef } = payload || {};
    return client.get(
      `/prompts/${encodeURIComponent(promptRef)}/versions/${encodeURIComponent(promptVersionRef)}/graphs`,
      {
        headers: reqHeaders,
        promptRequestMeta: { promptRef, promptVersionRef }
      }
    );
  }

  async updatePrompt({ payload, client, reqHeaders }) {
    const { promptRef, version, promptStart, promptEnd, name, description, comments, isAgentic = true } = payload;
    const requestBody = {
      version,
      promptStart,
      promptEnd,
      name,
      description,
      comments,
      isAgentic
    };
    return client.put(`/prompts/${encodeURIComponent(promptRef)}`, requestBody, {
      headers: reqHeaders,
      promptRequestMeta: { promptRef }
    });
  }

  handleResponse(response) {
    const store = Framework.getStoreRegistry().getStore(this.storeName);
    const requestMeta = response?.config?.promptRequestMeta || {};

    switch (this.serviceType) {
      case PROMPT_V2_SERVICE_TYPE.CATALOGUE:
        store.handleCatalogueResponse(response.data, {
          search: requestMeta.search,
          dataContext: requestMeta.dataContext || {}
        });
        return;
      case PROMPT_V2_SERVICE_TYPE.VERSION:
        store.handleVersionResponse(response.data, {
          promptRef: requestMeta.promptRef,
          promptVersionRef: requestMeta.promptVersionRef
        });
        return;
      case PROMPT_V2_SERVICE_TYPE.VERSIONS:
        store.handleVersionsResponse(response.data, {
          promptRef: this.responsePromptRefs?.get(response) ?? this.pendingPromptRef
        });
        return;
      case PROMPT_V2_SERVICE_TYPE.UPDATE:
        store[this.handleUpdateMethod](response.data, { promptRef: requestMeta.promptRef });
        break;
      case PROMPT_V2_SERVICE_TYPE.NAME_AVAILABILITY:
        store.handleNameAvailabilityResponse(response.data);
        return;
      case PROMPT_V2_SERVICE_TYPE.VERSION_GRAPHS:
        store.handleAssociatedGraphsResponse(response.data, {
          promptRef: requestMeta.promptRef,
          promptVersionRef: requestMeta.promptVersionRef
        });
        return;
      default:
        throw new Error(`Unsupported prompt v2 service type: ${this.serviceType}`);
    }
  }

  handleError(error) {
    handlePromptServiceError(error, this.errorLangKey, this.errorEvent, this.storeName);
  }
}
